import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Microscope, X, Wifi, Layers, Video, Navigation, Box, Activity, FileText, Send, ZoomIn, ZoomOut } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';

// --- VITAL: SETUP PDF WORKER ---
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// --- RESEARCH DATA ---
const researchData = [
  {
    id: 1,
    title: "3D Scene Reconstruction",
    area: "Computer Vision",
    status: "Ongoing",
    lead: "Prof. K.S. Venkatesh",
    desc: "Benchmarking state-of-the-art models via GitHub implementations. The project involves curating custom outdoor datasets and developing enhanced metrics to evaluate reconstruction accuracy.",
    icon: <Box size={24} className="text-blue-400" />,
    image: `${process.env.PUBLIC_URL}/images/3d scene.webp`,
    pdf: `${process.env.PUBLIC_URL}/docs/3dscene.pdf`,
    applyLink: "https://forms.gle/C8nwJ47cjtL2NioVA"
  },
  {
    id: 2,
    title: "Legged Robot Navigation",
    area: "Robotics & Control",
    status: "Published",
    lead: "Control Systems Group",
    desc: "A quadruped navigation system using Vision-Language-Action (VLA) models to interpret natural language commands and visual scenes. Bridges Gazebo simulation with hardware deployment.",
    icon: <Navigation size={24} className="text-orange-400" />,
    image: `${process.env.PUBLIC_URL}/images/legged.webp`,
    pdf: `${process.env.PUBLIC_URL}/docs/legged.pdf`,
    applyLink: "https://forms.gle/C8nwJ47cjtL2NioVA"
  },
  {
    id: 3,
    title: "Denoising & Artifact Localization",
    area: "Signal Processing",
    status: "Ongoing",
    lead: "Image Processing Lab",
    desc: "A physics-informed framework for denoising AFM/STM images without clean references. Uses blind-spot networks to preserve structure and weakly-supervised learning to localize artifacts.",
    icon: <Layers size={24} className="text-purple-400" />,
    image: `${process.env.PUBLIC_URL}/images/denoising.webp`,
    pdf: `${process.env.PUBLIC_URL}/docs/denoising.pdf`,
    applyLink: "https://forms.gle/C8nwJ47cjtL2NioVA"
  },
  {
    id: 4,
    title: "Video Relighting",
    area: "Computer Vision",
    status: "Thesis",
    lead: "Visual Computing Lab",
    desc: "Developing AI models for intrinsic video decomposition and relighting using physics-inspired priors. Focuses on edge-compute optimization and LoRA fine-tuning for efficient deployment.",
    icon: <Video size={24} className="text-pink-400" />,
    image: `${process.env.PUBLIC_URL}/images/video.webp`,
    pdf: `${process.env.PUBLIC_URL}/docs/relight.pdf`,
    applyLink: "https://forms.gle/C8nwJ47cjtL2NioVA"
  },
  {
    id: 5,
    title: "Drone Sound Classification",
    area: "Audio Processing",
    status: "Prototype",
    lead: "Acoustics Group",
    desc: "Benchmarking deep learning models like YAMNet, PANNs, and Transformers for long-range (1000m) drone detection. Involves dataset collection and optimizing metrics like F1-score and range accuracy.",
    icon: <Activity size={24} className="text-cyan-400" />,
    image: `${process.env.PUBLIC_URL}/images/drone sound.webp`,
    pdf: `${process.env.PUBLIC_URL}/docs/drone-nondrone.pdf`,
    applyLink: "https://forms.gle/C8nwJ47cjtL2NioVA"
  },
  {
    id: 6,
    title: "Underwater Depth Map Estimation",
    area: "Deep Learning",
    status: "Research",
    lead: "Marine Robotics Team",
    desc: "Benchmarking baseline models for depth estimation using datasets like UIEB and SQUID. Focuses on improving metrics for visual clarity and geometric consistency in turbid underwater environments.",
    icon: <Wifi size={24} className="text-teal-400" />,
    image: `${process.env.PUBLIC_URL}/images/underwater.webp`,
    pdf: `${process.env.PUBLIC_URL}/docs/underwater.pdf`,
    applyLink: "https://forms.gle/C8nwJ47cjtL2NioVA"
  }
];

const areas = ["All", "Computer Vision", "Robotics & Control", "Signal Processing", "Deep Learning"];

// --- SKELETON COMPONENT ---
const ResearchSkeleton = () => (
  <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-slate-100 flex flex-col h-full">
    <div className="h-56 bg-slate-200 animate-pulse" />
    <div className="p-6 flex-1 flex flex-col animate-pulse">
      <div className="w-24 h-3 bg-slate-200 rounded mb-2" />
      <div className="w-40 h-5 bg-slate-200 rounded mb-4" />
      <div className="space-y-2 mb-6">
        <div className="w-full h-3 bg-slate-100 rounded" />
        <div className="w-5/6 h-3 bg-slate-100 rounded" />
      </div>
      <div className="pt-4 border-t border-slate-50 flex gap-3">
        <div className="w-16 h-8 bg-slate-200 rounded-lg" />
        <div className="w-16 h-8 bg-slate-200 rounded-lg" />
      </div>
    </div>
  </div>
);

// --- MODAL COMPONENT ---
const CoolPDFModal = ({ isOpen, onClose, pdfUrl, title }) => {
  const [numPages, setNumPages] = useState(null);
  const [scale, setScale] = useState(1.0);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-slate-900/95 backdrop-blur-md p-4"
        onClick={onClose}
      >
        <div className="w-full max-w-5xl flex justify-between items-center text-white mb-4 px-4" onClick={e => e.stopPropagation()}>
          <h3 className="font-bold text-lg truncate opacity-90">{title}</h3>
          <div className="flex gap-4">
            <a href={pdfUrl} target="_blank" rel="noreferrer" className="text-sm font-bold text-slate-400 hover:text-white flex items-center gap-2 transition-colors">
              <FileText size={16}/> Download
            </a>
            <button onClick={onClose} className="hover:bg-white/10 p-2 rounded-full transition-colors">
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="flex-1 w-full max-w-5xl overflow-y-auto rounded-xl bg-slate-800/50 shadow-2xl p-4 md:p-8" onClick={e => e.stopPropagation()}>
          <Document
            file={pdfUrl}
            onLoadSuccess={({ numPages }) => setNumPages(numPages)}
            loading={<div className="text-white text-center mt-20">Loading Paper...</div>}
            className="flex flex-col items-center gap-4"
          >
            {Array.from(new Array(numPages), (el, index) => (
              <Page 
                key={`page_${index + 1}`}
                pageNumber={index + 1} 
                scale={scale} 
                renderTextLayer={false} 
                renderAnnotationLayer={false}
                width={window.innerWidth > 800 ? 800 : window.innerWidth - 60}
                className="shadow-lg"
              />
            ))}
          </Document>
        </div>

        <div className="absolute bottom-6 flex items-center gap-6 px-6 py-3 bg-slate-900/80 backdrop-blur-xl rounded-full border border-white/10 text-white z-50" onClick={e => e.stopPropagation()}>
          <button onClick={() => setScale(s => Math.max(0.5, s - 0.2))}><ZoomOut size={20} /></button>
          <span className="text-xs font-bold w-12 text-center">{Math.round(scale * 100)}%</span>
          <button onClick={() => setScale(s => Math.min(2.0, s + 0.2))}><ZoomIn size={20} /></button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// --- MAIN COMPONENT ---
const Research = () => {
  const [filter, setFilter] = useState("All");
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulated loading time to ensure the UI feels smooth
    const timer = setTimeout(() => setIsLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  const filteredData = researchData.filter(item => 
    filter === "All" || item.area === filter
  );

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-20 font-sans">
      <CoolPDFModal 
        isOpen={!!selectedPdf} 
        onClose={() => setSelectedPdf(null)} 
        pdfUrl={selectedPdf?.url} 
        title={selectedPdf?.title}
      />

      <div className="container mx-auto px-6">
        <div className="text-center mb-16 max-w-4xl mx-auto">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-100 text-violet-700 text-xs font-bold uppercase tracking-widest mb-4"
          >
            <Microscope size={14} /> Academic Frontiers
          </motion.div>
          <motion.h1 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl font-extrabold text-slate-900 mb-6"
          >
            Research & <span className="text-violet-600">Innovation</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-slate-600 text-xl"
          >
            Exploring the unknown. From reconstructing 3D worlds to teaching robots how to walk, dive into the cutting-edge research conducted at IITK.
          </motion.p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {areas.map((area) => (
            <button
              key={area}
              onClick={() => setFilter(area)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                filter === area 
                  ? 'bg-violet-600 text-white shadow-lg shadow-violet-200' 
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              {area}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 min-h-[400px]">
          <AnimatePresence mode="popLayout">
            {isLoading ? (
              // Display 6 skeletons while loading
              [...Array(6)].map((_, i) => (
                <motion.div key={`skeleton-${i}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <ResearchSkeleton />
                </motion.div>
              ))
            ) : (
              filteredData.map((project, idx) => (
                <motion.div 
                  key={project.id}
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.3, delay: idx * 0.05 }}
                  className="bg-white rounded-2xl overflow-hidden shadow-lg border border-slate-100 group flex flex-col h-full hover:shadow-2xl transition-all duration-300"
                >
                  <div className="h-56 overflow-hidden relative bg-slate-200">
                    <img 
                        src={project.image} 
                        alt={project.title} 
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 text-white">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="p-1.5 bg-white/20 backdrop-blur-md rounded-lg">{project.icon}</span>
                        <span className="text-xs font-bold uppercase tracking-wider bg-violet-600 px-2 py-0.5 rounded-sm">
                          {project.status}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold leading-tight">{project.title}</h3>
                    </div>
                  </div>
                  
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="mb-4">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Research Area</span>
                      <p className="text-violet-600 font-semibold">{project.area}</p>
                    </div>
                    <p className="text-slate-600 text-sm leading-relaxed mb-6 flex-1">
                      {project.desc}
                    </p>
                    
                    <div className="pt-4 border-t border-slate-50 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => setSelectedPdf({ url: project.pdf, title: project.title })}
                          className="text-violet-600 hover:text-violet-700 hover:bg-violet-50 p-2 rounded-lg text-xs font-bold flex items-center gap-1 transition-all"
                        >
                          <FileText size={16} /> Paper
                        </button>
                        <a 
                          href={project.applyLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-violet-600 hover:bg-violet-700 text-white px-3 py-2 rounded-lg text-xs font-bold flex items-center gap-1 transition-all shadow-md"
                        >
                          Apply <Send size={14} />
                        </a>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Research;